/**
 * 그 달 명세서를 받는 기사가 **기사앱으로 명세를 볼 수 있는지**(2026-10-20 팀장 전체회의 #39 — 10월 정산 실운영 · 앱 설치·권한 지원).
 * 「기사 명세서 공개」 창의 달 줄에서 연다. 판정은 서버(`salaryPublish.ts listSalaryAppReach`) — 기사앱 로그인 규칙과 같다.
 */

import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { DataTable, ErrorBox, dash, type Column, type Row } from '../components/DataTable';
import { kindLabel } from '../lib/driverKind';

type Status = 'ok' | 'never' | 'other' | 'blocked' | 'no_phone';

const STATUS: Record<Status, { label: string; tip: string; cls: string }> = {
  ok: { label: '로그인함', tip: '기사앱에 로그인한 기록이 있습니다', cls: 'text-emerald-700' },
  never: { label: '아직 로그인 안 함', tip: '번호는 맞습니다 — 설치·로그인 안내 대상', cls: 'text-amber-700' },
  other: {
    label: '다른 기사 줄로 로그인',
    tip: '같은 번호의 다른 기사 줄로 들어가 이 명세가 앱에 안 보입니다 — 기준정보에서 중복 기사 줄 정리',
    cls: 'text-red-700',
  },
  blocked: {
    label: '번호 겹침(로그인 막힘)',
    tip: '같은 번호 기사가 여럿이라 인증번호가 안 갑니다 — 기준정보에서 번호 정리',
    cls: 'text-red-700',
  },
  no_phone: { label: '번호 없음', tip: '연락처가 휴대폰 번호가 아니라 인증번호를 못 받습니다', cls: 'text-red-700' },
};
const ORDER: Status[] = ['never', 'other', 'blocked', 'no_phone', 'ok'];

export default function SalaryAppReach({ driveMon, onClose }: { driveMon: string; onClose: () => void }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pick, setPick] = useState<Status | 'all'>('all');

  useEffect(() => {
    api.listSalaryAppReach({ driveMon })
      .then((r) => setRows(r.rows as unknown as Row[]))
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : '불러오지 못했습니다.');
        setRows([]);
      });
  }, [driveMon]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of rows ?? []) c[String(r.status)] = (c[String(r.status)] ?? 0) + 1;
    return c;
  }, [rows]);
  const pushCnt = useMemo(() => (rows ?? []).filter((r) => r.push === true).length, [rows]);
  const shown = useMemo(() => (pick === 'all' ? rows ?? [] : (rows ?? []).filter((r) => r.status === pick)), [rows, pick]);

  const columns: Column[] = [
    { key: 'driver_name', label: '기사', phone: 'pin', copy: (r) => String(r.driver_name ?? '') },
    { key: 'driver_kind', label: '구분', render: (r) => kindLabel(r.driver_kind) },
    { key: 'car_no', label: '차량', render: (r) => dash(r.car_no) },
    { key: 'phone', label: '연락처', copy: (r) => String(r.phone ?? ''), render: (r) => dash(r.phone) },
    {
      key: 'status',
      label: '상태',
      render: (r) => {
        const s = STATUS[r.status as Status];
        return s ? <span className={`font-semibold ${s.cls}`} title={s.tip}>{s.label}</span> : dash(r.status);
      },
    },
    { key: 'logged_in_at', label: '마지막 로그인', render: (r) => dash(r.logged_in_at) },
    { key: 'push', label: '알림', render: (r) => (r.push ? '허용' : '—') },
  ];

  const total = rows?.length ?? 0;
  const chip = (k: Status | 'all', label: string, n: number) => (
    <button
      key={k}
      className={`rounded-full border px-3 py-1 text-sm ${pick === k ? 'border-brand-600 bg-brand-50 font-semibold' : 'border-line'}`}
      onClick={() => setPick(k)}
    >
      {label} {n.toLocaleString('ko-KR')}
    </button>
  );

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <h2 className="text-lg font-bold">{driveMon} 기사앱 현황</h2>
          <p className="mt-1 text-sm text-ink-soft">
            {rows === null
              ? '세는 중…'
              : `이 달 명세서가 있는 기사 ${total.toLocaleString('ko-KR')}명 중 로그인 ${(counts.ok ?? 0).toLocaleString('ko-KR')}명 · 알림 허용 ${pushCnt.toLocaleString('ko-KR')}명.`}{' '}
            빨간 상태는 앱을 설치해도 명세를 못 봅니다(상태에 마우스를 올리면 고칠 곳).
          </p>
        </div>
        <button className="btn-ghost ml-auto" onClick={onClose}>
          닫기
        </button>
      </div>

      {error && <ErrorBox message={error} />}

      <div className={`flex flex-wrap gap-2 ${rows === null ? 'invisible' : ''}`}>
        {chip('all', '전체', total)}
        {ORDER.map((k) => chip(k, STATUS[k].label, counts[k] ?? 0))}
      </div>

      <DataTable
        columns={columns}
        rows={shown}
        loading={rows === null}
        exportXlsx={{ name: `기사앱현황_${driveMon}${pick === 'all' ? '' : `_${STATUS[pick].label}`}` }}
      />
    </div>
  );
}
